import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// Constantes de tempo
const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutos em ms
const ABSOLUTE_TIMEOUT = 24 * 60 * 60 * 1000; // 1 dia em ms
const CHECK_INTERVAL = 30 * 1000; // Verificar a cada 30 segundos

export const useClientSession = () => {
  const navigate = useNavigate();
  const lastActivityRef = useRef<number>(Date.now());
  const sessionStartRef = useRef<number>(Date.now());
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Função de logout
  const logout = useCallback((reason?: string) => {
    // Limpar todos os dados do localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('userName');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('userData');
    localStorage.removeItem('userPlan');
    localStorage.removeItem('session_start');
    
    // Mostrar mensagem apropriada
    if (reason === 'inactivity') {
      toast.info('Sua sessão expirou por inatividade. Por favor, faça login novamente.');
    } else if (reason === 'expired') {
      toast.info('Sua sessão expirou. Por favor, faça login novamente.');
    }
    
    // Redirecionar para login
    navigate('/');
  }, [navigate]);

  // Atualizar última atividade
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Verificar se a sessão deve expirar
  const checkSession = useCallback(() => {
    const token = localStorage.getItem('token');
    
    // Se não tem token, não precisa verificar
    if (!token) {
      return;
    }

    const now = Date.now();
    const timeSinceActivity = now - lastActivityRef.current;
    const timeSinceStart = now - sessionStartRef.current;

    // Verificar inatividade (10 minutos)
    if (timeSinceActivity >= INACTIVITY_TIMEOUT) {
      logout('inactivity');
      return;
    }

    // Verificar tempo absoluto (1 dia)
    if (timeSinceStart >= ABSOLUTE_TIMEOUT) {
      logout('expired');
      return;
    }
  }, [logout]);

  useEffect(() => {
    // Recuperar ou definir o início da sessão
    const savedSessionStart = localStorage.getItem('session_start');
    if (savedSessionStart) {
      sessionStartRef.current = parseInt(savedSessionStart, 10);
      
      // Verificar se já passou do tempo absoluto
      if (Date.now() - sessionStartRef.current >= ABSOLUTE_TIMEOUT) {
        logout('expired');
        return;
      }
    } else {
      // Nova sessão - salvar o início
      const now = Date.now();
      sessionStartRef.current = now;
      localStorage.setItem('session_start', now.toString());
    }

    // Lista de eventos que indicam atividade do usuário
    const activityEvents = [
      'mousemove',
      'mousedown',
      'keydown',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'wheel'
    ];

    // Adicionar listeners de atividade
    activityEvents.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    // Iniciar verificação periódica
    checkIntervalRef.current = setInterval(checkSession, CHECK_INTERVAL);

    // Verificar imediatamente ao montar
    checkSession();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [updateActivity, checkSession, logout]);

  // Retornar função de logout manual se necessário
  return { logout };
};

export default useClientSession;

